import {Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges} from "@angular/core";
import {EMPTY_FINANCIAL_PERFORMANCE_RECORD, FinancialPerformanceRecord} from "../../../models/asset/financial-performance-record";
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {ActionType, AssetHistoryPeriodDialogComponent} from "../asset-history-period-dialog/asset-history-period-dialog.component";
import {Store} from "@ngrx/store";
import {MatDialog} from "@angular/material/dialog";
import {DecimalFormatPipe} from "../../../../shared/pipes/decimal-format/decimal-format.pipe";
import {PeriodType, TraceableDate, TraceableMoney, TraceablePeriod} from "../../../../shared/model/traceable";
import {PeriodTypeEnum} from "../../../models/asset/period-type.enum";
import {AssetFinancialHistoryActions} from "../../../store/asset.actions";
import {DateTime} from "luxon";
import {selectGeneratedAvailablePeriodDates, selectSelectedAsset, selectValidateAvailablePeriodDates} from "../../../store/asset/asset.selectors";
import {map, Observable, of} from "rxjs";
import {AvailablePeriodDates, Period} from "../../../../shared/model/period";
import {CodeTableEnum} from "../../../../shared/model/code";

@Component({
    selector: "valumize-asset-fin-data-history-table",
    templateUrl: "./asset-fin-data-history-table.component.html",
    styleUrls: ["./asset-fin-data-history-table.component.scss"],
    standalone: false
})
export class AssetFinDataHistoryTableComponent implements OnInit, OnChanges {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);

    @Input() selectedAssetFinancialPerformanceData: FinancialPerformanceRecord[] = [];
    @Input() baselineAssetFinancialPerformanceData: FinancialPerformanceRecord[] = [];
    @Input() isEditable = false;
    @Input() isDataset = false;
    @Input() isEditDisabled: boolean | null = null;
    @Input() exitDateAssumption: TraceableDate | null = null;
    @Input() reportDate: TraceableDate | null = null;

    @Output() recordsToSave = new EventEmitter<FinancialPerformanceRecord[]>();
    @Output() recordsForCalc = new EventEmitter<FinancialPerformanceRecord[]>();
    @Output() triggerEdit = new EventEmitter();
    @Output() triggerCancel = new EventEmitter();
    @Output() periodTypesChange = new EventEmitter<PeriodType[]>();
    @Output() gpProjectedFilterChange = new EventEmitter<boolean>();

    codeTableFiscalYearEnd = CodeTableEnum.SHARED_FISCALYEAREND;

    fiscalYearEndMonth: string | undefined;

    periodTypesFilter: {periodType: PeriodType; displayName: string}[] = [
        {periodType: "FULL_FISCAL_YEAR", displayName: "FY"},
        {periodType: "SEMESTER", displayName: "H"},
        {periodType: "QUARTER", displayName: "Q"},
        {periodType: "LAST_TWELVE_MONTHS", displayName: "LTM"},
        {periodType: "YEAR_TO_DATE", displayName: "YTD"}
    ];
    selectedPeriodTypes: PeriodType[] = ["FULL_FISCAL_YEAR", "SEMESTER", "QUARTER", "LAST_TWELVE_MONTHS", "YEAR_TO_DATE"];
    allPeriodTypes: PeriodType[] = ["FULL_FISCAL_YEAR", "SEMESTER", "QUARTER", "LAST_TWELVE_MONTHS", "YEAR_TO_DATE"];

    showGpProjected = false;

    financialDataForms?: FormGroup;
    displayedColumns: string[] = [];
    selectedMetrics: string[] = [];
    alwaysDisplayRows = [
        "revenue",
        "revenueGrowth",
        "debtRepaymentCapacity",
        "netDebt",
        "enterpriseValue",
        "commonEquity"
    ];
    rowOrder: string[] = [
        "revenue",
        "revenueGrowth",
        "ebitdar",
        "ebitdarMargin",
        "ebitdarGrowth",
        "enterpriseValueOverEbitdarMultiple",
        "ebitda",
        "ebitdaMargin",
        "ebitdaGrowth",
        "enterpriseValueOverEbitdaMultiple",
        "ebita",
        "ebitaMargin",
        "ebitaGrowth",
        "enterpriseValueOverEbitaMultiple",
        "ebit",
        "ebitMargin",
        "ebitGrowth",
        "enterpriseValueOverEbitMultiple",
        "ebt",
        "ebtMargin",
        "ebtGrowth",
        "priceOverEarningsMultiple",
        "debtRepaymentCapacity",
        "netDebt",
        "enterpriseValue",
        "commonEquity"
    ];
    fieldDisplayNames: { [key: string]: string } = {
        revenue: "Revenue",
        revenueGrowth: "Revenue Growth",
        debtRepaymentCapacity: "Debt Repayment Capacity",
        netDebt: "Net Debt",
        enterpriseValue: "Enterprise Value",
        commonEquity: "Common Equity",
        ebt: "EBT",
        ebtMargin: "EBT Margin",
        ebtGrowth: "EBT Growth",
        ebit: "EBIT",
        ebitMargin: "EBIT Margin",
        ebitGrowth: "EBIT Growth",
        ebita: "EBITA",
        ebitaMargin: "EBITA Margin",
        ebitaGrowth: "EBITA Growth",
        ebitda: "EBITDA",
        ebitdaMargin: "EBITDA Margin",
        ebitdaGrowth: "EBITDA Growth",
        ebitdar: "EBITDAR",
        ebitdarMargin: "EBITDAR Margin",
        ebitdarGrowth: "EBITDAR Growth",
        priceOverEarningsMultiple: "P/E",
        enterpriseValueOverEbitMultiple: "EV/EBIT",
        enterpriseValueOverEbitaMultiple: "EV/EBITA",
        enterpriseValueOverEbitdaMultiple: "EV/EBITDA",
        enterpriseValueOverEbitdarMultiple: "EV/EBITDAR",
        enterpriseValueOverRevenueMultiple: "EV/Revenue"
    };
    fieldNames = Object.keys(this.fieldDisplayNames);

    // Metrics definitions for dynamic display in the table
    metrics = [
        {
            key: "ebt",
            displayName: "EBT",
            associatedRows: ["ebt", "ebtMargin", "ebtGrowth", "priceOverEarningsMultiple"]
        },
        {
            key: "ebit",
            displayName: "EBIT",
            associatedRows: ["ebit", "ebitMargin", "ebitGrowth", "enterpriseValueOverEbitMultiple"]
        },
        {
            key: "ebita",
            displayName: "EBITA",
            associatedRows: ["ebita", "ebitaMargin", "ebitaGrowth", "enterpriseValueOverEbitaMultiple"]
        },
        {
            key: "ebitda",
            displayName: "EBITDA",
            associatedRows: ["ebitda", "ebitdaMargin", "ebitdaGrowth", "enterpriseValueOverEbitdaMultiple"]
        },
        {
            key: "ebitdar",
            displayName: "EBITDAR",
            associatedRows: ["ebitdar", "ebitdarMargin", "ebitdarGrowth", "enterpriseValueOverEbitdarMultiple"]
        }
    ];

    protected readonly actionType = ActionType;

    constructor( ) {
    }

    availablePeriodDates: AvailablePeriodDates = {
        QUARTER: [],
        SEMESTER: [],
        FULL_FISCAL_YEAR: []
    };

    ngOnInit() {
        this.periodTypesChange.emit(this.selectedPeriodTypes);
        this.gpProjectedFilterChange.emit(this.showGpProjected);

        const formGroups = this.selectedAssetFinancialPerformanceData.length !== 0
            ? this.selectedAssetFinancialPerformanceData.map(record => this.createFinancialHistoryFormGroup(record))
            : [];

        // Initialize the form group for financial data forms with an array of the created form groups
        this.financialDataForms = this.formBuilder.group({records: this.formBuilder.array(formGroups)});

        if (this.isEditable) {
            this.financialDataForms?.enable();
        } else {
            this.financialDataForms?.disable();
        }

        // Determine the columns to be displayed based on the key dates available in the data
        this.displayedColumns = ["rowName"].concat(this.selectedAssetFinancialPerformanceData
            .filter(record => record.period.keyDate !== undefined && record.period.type !== undefined)
            .map(record => this.columnKey(record)));

        // Update the selected metrics based on the content of the metrics
        // if no records, default to 'ebitda'
        if (this.selectedAssetFinancialPerformanceData.length === 0) {
            this.selectedMetrics = ["ebitda"];
        } else {
            this.selectedMetrics = this.metrics
                .filter(metric => this.metricHasContent(metric.key))
                .map(metric => metric.key);
        }

        // Call the method to update the rows that will be displayed
        this.updateDisplayedRows();

        this.store.select(selectSelectedAsset).pipe(map(asset =>
            this.fiscalYearEndMonth = asset.data.fiscalYearEnd.code
        )).subscribe();

        this.store.select(selectGeneratedAvailablePeriodDates).pipe(map(availablePeriodDates =>
            this.availablePeriodDates = availablePeriodDates
        )).subscribe();
    }

    ngOnChanges(changes: SimpleChanges) {
        this.selectedAssetFinancialPerformanceData = changes["selectedAssetFinancialPerformanceData"]?.currentValue ?? this.selectedAssetFinancialPerformanceData;
        this.baselineAssetFinancialPerformanceData = changes["baselineAssetFinancialPerformanceData"]?.currentValue ?? this.baselineAssetFinancialPerformanceData;
        this.isEditable = changes["isEditable"]?.currentValue ?? this.isEditable;
        this.isDataset = changes["isDataset"]?.currentValue ?? this.isDataset;
        this.isEditDisabled = changes["isEditDisabled"]?.currentValue ?? this.isEditDisabled;

        this.ngOnInit();
    }

    private createFinancialHistoryFormGroup(record: FinancialPerformanceRecord): FormGroup {
        return this.formBuilder.group({
            periodType: [record.period.type ?? ""],
            periodKeyDate: [record.period.keyDate ?? ""],
            revenue: [DecimalFormatPipe.transformFromMillionsNum(record.revenue.amount ?? undefined)],
            netDebt: [DecimalFormatPipe.transformFromMillionsNum(record.netDebt.amount ?? undefined)],
            ebt: [DecimalFormatPipe.transformFromMillionsNum(record.ebt.amount ?? undefined)],
            ebit: [DecimalFormatPipe.transformFromMillionsNum(record.ebit.amount ?? undefined)],
            ebita: [DecimalFormatPipe.transformFromMillionsNum(record.ebita.amount ?? undefined)],
            ebitda: [DecimalFormatPipe.transformFromMillionsNum(record.ebitda.amount ?? undefined)],
            ebitdar: [DecimalFormatPipe.transformFromMillionsNum(record.ebitdar.amount ?? undefined)],
            enterpriseValue: [DecimalFormatPipe.transformFromMillionsNum(record.enterpriseValue.amount ?? undefined)],
        });
    }

    save() {
        if (this.financialDataForms?.valid) {
            const formRecords = (this.financialDataForms?.get("records") as FormArray).controls;

            // Ensure there are no duplicate key dates (including gpProjected dimension)
            const keyDates = formRecords.map((group, idx) => {
                const record = this.selectedAssetFinancialPerformanceData[idx];
                return group.get("periodKeyDate")?.value + group.get("periodType")?.value + (record?.gpProjected ? "_P" : "");
            });
            const uniqueKeyDates = new Set(keyDates);
            if (uniqueKeyDates.size !== keyDates.length) {
                this.store.dispatch(AssetFinancialHistoryActions.saveError({errorMsg: "Duplicate key dates are not allowed."}));
                return;
            }

            const transformAmount = (field: string, group: AbstractControl) => DecimalFormatPipe.transformToMillionsNum(group.get(field)?.value);

            const mapPerformanceData = (record: FinancialPerformanceRecord, group: AbstractControl) => {
                return {
                    revenue: {...record.revenue, amount: transformAmount("revenue", group)},
                    netDebt: {...record.netDebt, amount: transformAmount("netDebt", group)},
                    ebt: {...record.ebt, amount: transformAmount("ebt", group)},
                    ebit: {...record.ebit, amount: transformAmount("ebit", group)},
                    ebita: {...record.ebita, amount: transformAmount("ebita", group)},
                    ebitda: {...record.ebitda, amount: transformAmount("ebitda", group)},
                    ebitdar: {...record.ebitdar, amount: transformAmount("ebitdar", group)},
                    enterpriseValue: {...record.enterpriseValue, amount: transformAmount("enterpriseValue", group)},
                };
            };

            const recordsToSave: FinancialPerformanceRecord[] = formRecords.map((group, index) => {
                const record = this.selectedAssetFinancialPerformanceData[index];
                return {
                    ...record,
                    period: {
                        ...record.period,
                        type: group.get("periodType")?.value,
                        keyDate: group.get("periodKeyDate")?.value
                    },
                    ...mapPerformanceData(record, group)
                };
            });
            this.recordsToSave.emit(recordsToSave);
        }
    }

    edit = () => {
        this.selectedPeriodTypes = [...this.allPeriodTypes];
        this.showGpProjected = true;
        this.periodTypesChange.emit(this.selectedPeriodTypes);
        this.gpProjectedFilterChange.emit(this.showGpProjected);
        this.triggerEdit.emit();
    }

    cancel = () => this.triggerCancel.emit();

    // retrieves the value of a field for a given key date
    getFieldValue(fieldName: string, isBaseline: boolean, keyDate?: string, type?: string, gpProjected?: boolean): any {
        const financialPerformanceRecords = isBaseline ? this.baselineAssetFinancialPerformanceData : this.selectedAssetFinancialPerformanceData;
        const record = financialPerformanceRecords.find(r =>
            r.period.keyDate === keyDate && r.period.type === type && r.gpProjected === (gpProjected ?? false)
        );
        return record ? (record as any)[fieldName] : null;
    }

    // calculates and updates a record based on a given key date and field name
    calc(keyDate?: string, type?: string, fieldName?: string, gpProjected?: boolean) {
        if (fieldName && keyDate && type) {
            // Find the financial record matching the given key date
            const recordToUpdate = this.selectedAssetFinancialPerformanceData.find(r =>
                r.period.keyDate === keyDate && r.period.type === type && r.gpProjected === (gpProjected ?? false)
            );
            if (!recordToUpdate) {
                console.error(`No record found for keyDate: ${keyDate}`);
                return;
            }

            // Update the financial record with the new value for the specified field
            const updatedRecord = this.updateFinancialHistoryRecord(recordToUpdate, fieldName, this.getFormControl(keyDate, type, fieldName, gpProjected).value);
            // Map over the financial history data and replace the record with the updated one
            const updatedRecords = this.selectedAssetFinancialPerformanceData.map(r =>
                r.period.keyDate === keyDate && r.period.type === type && r.gpProjected === (gpProjected ?? false) ? updatedRecord : r
            );

            this.recordsForCalc.emit(updatedRecords);
        } else {
            this.recordsForCalc.emit(this.selectedAssetFinancialPerformanceData);
        }
    }

    updateFinancialHistoryRecord(record: FinancialPerformanceRecord, fieldName: string, newValue: any): FinancialPerformanceRecord {
        if (["revenue", "ebitdar", "ebitda", "ebita", "ebit", "ebt", "netDebt", "enterpriseValue"].includes(fieldName)) {
            return {
                ...(record),
                [fieldName as keyof FinancialPerformanceRecord]: {
                    ...(record as any)[fieldName as keyof FinancialPerformanceRecord],
                    amount: DecimalFormatPipe.transformToMillionsNum(newValue)
                }
            };
        } else {
            return record;
        }
    }

    getFormControl(keyDate: string | undefined, type: string | undefined, fieldName: string, gpProjected?: boolean): FormControl {
        const index = this.selectedAssetFinancialPerformanceData.findIndex(r =>
            r.period.keyDate === keyDate && r.period.type === type && r.gpProjected === (gpProjected ?? false)
        );
        const formArray = this.financialDataForms?.get("records") as FormArray;
        const control = (formArray?.at(index) as FormGroup)?.get(fieldName);

        if (!control) {
            throw new Error(`FormControl not found for keyDate: ${keyDate} and fieldName: ${fieldName}`);
        }
        return control as FormControl;
    }

    validatePeriodDate(date?: string, type?: Period["type"]): Observable<boolean> {
        if (!!date && !!type && !this.isRollingPeriod(type)) {
            return this.store.select(selectValidateAvailablePeriodDates(date, type));
        } else if (!!date && this.isRollingPeriod(type)){
            return of(true);
        }
        return of(false);
    }

    isRollingPeriod(periodType?: Period["type"]): boolean {
        return periodType === PeriodTypeEnum.LastTwelveMonths || periodType === PeriodTypeEnum.YearToDate;
    }

    openPeriodSelectorDialog(action: ActionType, index?: number): void {
        let periodKeyDate: string | undefined;
        let periodType: PeriodTypeEnum | undefined;
        let gpProjected = false;
        let existingPeriods = this.getExistingPeriodsFromHistoryData();

        if (action === ActionType.Edit && index != null && index >= 0) {
            const recordFormGroup = (this.financialDataForms?.get("records") as FormArray)?.at(index) as FormGroup;
            periodType = recordFormGroup.get("periodType")?.getRawValue();
            periodKeyDate = recordFormGroup.get("periodKeyDate")?.getRawValue();
            gpProjected = this.selectedAssetFinancialPerformanceData[index]?.gpProjected ?? false;
            existingPeriods = existingPeriods.filter(p => p.periodKeyDate !== periodKeyDate || p.gpProjected !== gpProjected);
        }

        const filteredAvailablePeriodDates: AvailablePeriodDates = this.filterAvailablePeriodDates(existingPeriods, gpProjected);
        const finalAvailablePeriodDates: AvailablePeriodDates = this.getFinalAvailablePeriodDates(filteredAvailablePeriodDates);

        const dialogRef = this.dialog.open(AssetHistoryPeriodDialogComponent, {
            data: {
                action,
                periodType,
                periodKeyDate,
                gpProjected,
                availablePeriodDates: finalAvailablePeriodDates,
                isFullFiscalYearOnly: false
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                switch (result.action) {
                    case ActionType.Edit:
                        this.updateRecord(result);
                        break;
                    case ActionType.Add:
                        this.addRecord(result);
                        break;
                    case ActionType.Delete:
                        this.deleteRecord(result);
                        break;
                }
                this.calc();
            }
        });
    }

    private filterAvailablePeriodDates(existingPeriods: { periodType: string | undefined; periodKeyDate: string | undefined; gpProjected: boolean }[], gpProjected: boolean) {
        return {
            QUARTER: this.availablePeriodDates.QUARTER.filter(date =>
                !existingPeriods.some(p => p.periodKeyDate === date && p.periodType === "QUARTER" && p.gpProjected === gpProjected)),
            SEMESTER: this.availablePeriodDates.SEMESTER.filter(date =>
                !existingPeriods.some(p => p.periodKeyDate === date && p.periodType === "SEMESTER" && p.gpProjected === gpProjected)),
            FULL_FISCAL_YEAR: this.availablePeriodDates.FULL_FISCAL_YEAR.filter(date =>
                !existingPeriods.some(p => p.periodKeyDate === date && p.periodType === "FULL_FISCAL_YEAR" && p.gpProjected === gpProjected))
        };
    }

    private getFinalAvailablePeriodDates(filteredAvailablePeriodDates: AvailablePeriodDates): AvailablePeriodDates {
        const dateFilter = (date: string) => new Date(date) <= new Date();
        return {
            QUARTER: filteredAvailablePeriodDates.QUARTER.filter(dateFilter),
            SEMESTER: filteredAvailablePeriodDates.SEMESTER.filter(dateFilter),
            FULL_FISCAL_YEAR: filteredAvailablePeriodDates.FULL_FISCAL_YEAR.filter(dateFilter)
        };
    }

    private updateRecord(result: any) {
        this.selectedAssetFinancialPerformanceData = this.selectedAssetFinancialPerformanceData.map(r =>
            r.period.keyDate === result.originPeriodKeyDate
            && r.period.type === result.originPeriodType
            && r.gpProjected === (result.originGpProjected ?? false)
                ? {...r, period: {...r.period, keyDate: result.periodKeyDate, type: result.periodType}, gpProjected: result.gpProjected ?? r.gpProjected}
                : r
        );
    }

    private addRecord(result: any) {
        const newRecord: FinancialPerformanceRecord = {
            ...EMPTY_FINANCIAL_PERFORMANCE_RECORD,
            period: {...EMPTY_FINANCIAL_PERFORMANCE_RECORD.period, type: result.periodType, keyDate: result.periodKeyDate},
            gpProjected: result.gpProjected ?? false
        };
        this.selectedAssetFinancialPerformanceData.push(newRecord);
    }

    private deleteRecord(result: any) {
        const recIndex = this.selectedAssetFinancialPerformanceData.findIndex(r =>
            r.period.keyDate === result.originPeriodKeyDate
            && r.period.type === result.originPeriodType
            && r.gpProjected === (result.gpProjected ?? false)
        );
        if (recIndex !== -1) {
            this.selectedAssetFinancialPerformanceData.splice(recIndex, 1);
        }
    }

    getExistingPeriodsFromHistoryData(): { periodType: string | undefined; periodKeyDate: string | undefined; gpProjected: boolean }[] {
        return this.selectedAssetFinancialPerformanceData.map(record => ({
            periodType: record.period.type,
            periodKeyDate: record.period.keyDate,
            gpProjected: record.gpProjected
        }));
    }

    getPeriodFromFormGroup(index: number): TraceablePeriod {
        const recordsArray = this.financialDataForms?.get("records") as FormArray;
        const recordFormGroup = recordsArray?.at(index) as FormGroup;
        return {type: recordFormGroup.get("periodType")?.value, keyDate: recordFormGroup.get("periodKeyDate")?.value};
    }

    isFieldEditable(fieldName: string): boolean {
        const editableFields = ["revenue", "netDebt", "ebt", "ebit", "ebita", "ebitda", "ebitdar", "enterpriseValue"];
        return editableFields.includes(fieldName);
    }

    styleRowImportant(fieldName: string): string {
        const importantRowTypes = ["revenue", "netDebt", "ebt", "ebit", "ebita", "ebitda", "ebitdar"];
        return importantRowTypes.includes(fieldName) ? "row-important" : "";
    }

    toggleMetric(metric: { key: string }) {
        const index = this.selectedMetrics.indexOf(metric.key);
        if (index > -1) {
            this.selectedMetrics.splice(index, 1);
        } else {
            this.selectedMetrics.push(metric.key);
        }
        this.updateDisplayedRows();
    }

    isSelectedMetric(metric: { key: string }): boolean {
        return this.selectedMetrics.includes(metric.key);
    }


    toggleType(periodTypeFilter: {periodType: PeriodType}) {
        this.selectedPeriodTypes = this.selectedPeriodTypes.includes(periodTypeFilter.periodType)
            ? this.selectedPeriodTypes.filter(x => x !== periodTypeFilter.periodType)
            : [...this.selectedPeriodTypes, periodTypeFilter.periodType];
        this.periodTypesChange.emit(this.selectedPeriodTypes);
    }

    isSelectedType(periodTypeFilter: {periodType: PeriodType}): boolean {
        return this.selectedPeriodTypes.includes(periodTypeFilter.periodType);
    }

    toggleGpProjected() {
        this.showGpProjected = !this.showGpProjected;
        this.gpProjectedFilterChange.emit(this.showGpProjected);
    }

    columnKey(record: FinancialPerformanceRecord): string {
        return `${record.period.keyDate}${record.period.type}${record.gpProjected ? "_P" : ""}`;
    }

    updateDisplayedRows() {
        let rowsToShow = [...this.alwaysDisplayRows];

        for (const metricKey of this.selectedMetrics) {
            const metric = this.metrics.find(m => m.key === metricKey);
            if (metric) {
                rowsToShow = rowsToShow.concat(metric.associatedRows);
            }
        }
        rowsToShow = this.sortBy(rowsToShow);
        this.fieldNames = rowsToShow;
    }

    sortBy(data: string[]): any {
        const order = this.rowOrder;
        const ordering: any = {};
        for (let i = 0; i < order.length; i++) {
            ordering[order[i]] = i;
        }
        return data.sort((a: any, b: any) =>
            (ordering[a] - ordering[b]) || a.localeCompare(b)
        );
    }

    metricHasContent(metricKey: string): boolean {
        const hasContent = this.selectedAssetFinancialPerformanceData.some(record => {
            const value = (record as any)[metricKey]?.amount;
            return value !== null && value !== undefined;
        });

        // If this is the *only* selected metric, and there's no content yet, keep it
        if (!hasContent && this.selectedMetrics.length === 1 && this.selectedMetrics[0] === metricKey) {
            return true;
        }

        return hasContent;
    }

    overwriteValue(isEditable: boolean, control: FormControl, baselineValue: TraceableMoney) {
        if (isEditable) {
            control.setValue(DecimalFormatPipe.transformFromMillionsNum(baselineValue.amount ?? undefined));
        }
    }

    getChipColor(control: FormControl, baselineValue: TraceableMoney, value: TraceableMoney) {
        const baselineAmount = DecimalFormatPipe.transformFromMillionsNum(baselineValue.amount ?? undefined);
        const amount = DecimalFormatPipe.transformFromMillionsNum(value.amount ?? undefined);

        if (control.value === baselineAmount && baselineAmount !== amount) {
            return "chip-overwritten-value";
        } else if (baselineAmount === amount) {
            return "chip-same-value";
        } else {
            return "chip-diff-value";
        }
    }

    getLastModified(): string | null {
        let mostRecentDate: DateTime | null = null;

        for (const record of this.selectedAssetFinancialPerformanceData) {
            for (const field of this.fieldNames) {
                const currentField = (record as any)[field];

                if (currentField?.modDate) {
                    const currentModDate = DateTime.fromISO(currentField.modDate);

                    if (!mostRecentDate || currentModDate > mostRecentDate) {
                        mostRecentDate = currentModDate;
                    }
                }
            }
        }
        return mostRecentDate ? mostRecentDate.toISO() : null;
    }

    formatDate(keyDate: string | undefined): string {
        if (!keyDate) {
            return "";
        }
        return DateTime.fromISO(keyDate).toFormat("dd/MM/yyyy");
    }
}
